import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Checkbox from '@/components/ui/Checkbox'
import PriceInput from '@/components/ui/PriceInput'
import { MultipleRecurrenceSelect } from '@/features/recurrence'
import Input from '@/components/ui/Input'
import DurationInput from '@/components/ui/DurationInput'
import { Seperator } from '@/components/ui/Seperator'
import { FormData } from '../../hooks/useGoodieForm'
import Dropdown from '@/components/ui/Dropdown'
import { useEventButtonTypeOptions } from '../../utils/helpers'

export function initFields(goodie?: GraphQL.GoodieFragmentFragment) {
  if (!goodie) {
    return {
      hasLimitedSeats: false,
      isInstantApproval: false,
      canJoinDuringEvent: false,
      duration: 30 * 60_000,
      location: '',
      recurrences: [],
      button: GraphQL.EventButtonType.Hide
    }
  }

  switch (goodie?.__typename) {
    case 'Event': {
      const newEvent = { ...goodie.event }
      delete newEvent.__typename

      return {
        ...newEvent,
        recurrences: goodie.event.recurrences.map(rec => {
          const recurrence = {
            ...getFragment(SharedAPI.recurrence.RECURRENCE_FRAGMENT, rec)
          }
          delete recurrence.__typename

          return recurrence
        })
      }
    }
  }
}

export default function EventFields() {
  const { t } = useLingui()

  const buttonOptions = useEventButtonTypeOptions()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex max-w-full flex-row flex-wrap items-center justify-evenly gap-6">
        <Form.Field
          name="event.location"
          label={t`Location`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              placeholder={t({
                comment: 'Placeholder for the Location Input',
                message: 'e.g.: Spa-Reception'
              })}
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="event.price"
          label={`${t`Price (0 when free)`}*`}
          rules={{
            validate: {
              required(price) {
                if (price == null) return t`This field is required`
              }
            }
          }}
        >
          {({ renderProps }) => (
            <PriceInput
              placeholder={t({
                comment: 'Placeholder for the Price Input',
                message: 'e.g.: 100'
              })}
              {...renderProps}
            />
          )}
        </Form.Field>
      </div>
      <Form.Field
        name="event.duration"
        label={t`Duration`}
        rules={{
          required: t`This field is required`
        }}
      >
        {({ renderProps }) => <DurationInput {...renderProps} />}
      </Form.Field>
      <Form.Data<FormData>>
        {({ event: { duration } }) => {
          return (
            <Form.Field<FormData>
              name="event.recurrences"
              label={`${t`Times of the Event`}*`}
              rules={{
                validate: {
                  required(recurrences) {
                    if (!Array.isArray(recurrences) || recurrences.length < 1) {
                      return t`This field is required`
                    }
                  }
                }
              }}
            >
              {({ renderProps: { value, ...props } }) => (
                <MultipleRecurrenceSelect
                  recurrences={value}
                  generalDuration={duration}
                  {...props}
                />
              )}
            </Form.Field>
          )
        }}
      </Form.Data>
      <Form.Field
        name="event.canJoinDuringEvent"
        label={t`Can be joined during event`}
      >
        {({ renderProps: { value, onChange, ...props } }) => (
          <Checkbox
            checked={value}
            onChange={() => onChange(!value)}
            {...props}
          >
            <Trans>
              Guests are still able to join the event while it is already
              happening. For example when doing a Happy Hour.
            </Trans>
          </Checkbox>
        )}
      </Form.Field>
      <Seperator heading={t`Request options`} />
      <Form.Field<FormData>
        name="event.button"
        label={t`Request Button`}
        description={t`What should happen when a guest clicks on the request button.`}
      >
        {({ renderProps: props }) => (
          <Dropdown options={buttonOptions} {...props} />
        )}
      </Form.Field>
      <Form.Data<FormData>>
        {({ event: { button } }) => {
          switch (button) {
            case GraphQL.EventButtonType.Bookable:
              return <BookableFields />

            case GraphQL.EventButtonType.Hide:
              return null
          }
        }}
      </Form.Data>
    </div>
  )
}

function BookableFields() {
  const { t } = useLingui()

  return (
    <>
      <Form.Data<FormData>>
        {({ event: { hasLimitedSeats } }, { setValue, readOnly }) => {
          return (
            <Form.Field<FormData>
              name="event.seats"
              resetOnUnmount
              label={t`Limited Seats`}
              canBeDisabled={!readOnly}
              isInitialEnabled={hasLimitedSeats}
              onEnabledChange={hasLimitedSeats => {
                setValue('event.hasLimitedSeats', hasLimitedSeats, {
                  shouldValidate: true,
                  shouldDirty: true
                })

                if (!hasLimitedSeats) {
                  setValue('event.seats', null, {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
              }}
              rules={{
                validate: {
                  required(seats, { event: { hasLimitedSeats } }) {
                    if (hasLimitedSeats && !seats) {
                      return t`When enabling limited seats, an event should have at least 1 seat.`
                    }
                  }
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(parseInt(event.target.value))}
                  type="number"
                  placeholder={t`e.g.: 20`}
                  {...props}
                />
              )}
            </Form.Field>
          )
        }}
      </Form.Data>
      <Form.Field name="event.isInstantApproval" resetOnUnmount>
        {({ renderProps: { value, onChange, ...props } }) => (
          <Checkbox
            checked={value}
            onChange={() => onChange(!value)}
            {...props}
          >
            <Trans comment="Checkbox label, where the user can define if GuestGoodie should automatically approve guest booking of events. It only approves when the seats are not limited or when the booking doesn't exceed the event">
              Automatically accepts users to the event, if the seats are not
              limited or not exceeded.
            </Trans>
          </Checkbox>
        )}
      </Form.Field>
    </>
  )
}
